import React, { useState, useRef, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import HamburguerMenu from 'components/HamburguerMenu/HamburguerMenu'
import {
  Container,
  DesktopLayout,
  MobileLayout,
  HeaderRow,
  LeftGroup,
  GlobalActionsWrapper
} from './ReleasesMenu.styles'
import MenuOptions from './MenuOptions'
import GlobalActionMenu from '../GlobalActionMenu/GlobalActionMenu'
import { useDesktopMediaQuery } from 'utils/hooks/useMediaQueries'
import ReleasesSearchBar from './ReleasesSearchBar'
import { useSearchTracking } from '../../containers/Search/hooks/useSearchTracking'
import {
  SEARCH_TRACKING_EVENTS,
  SEARCH_ACTIONS,
  SEARCH_PLATFORMS
} from '../../constants/searchTracking'
import { checkFlag } from '@dsny/dsny-feature-flag-library'
import { useFeatureFlags } from 'contexts/FeatureFlagsContext'
import debounce from 'lodash/debounce'

function ReleasesMenu({ searched: searchedProp, onSearchingChange }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const isSearchRoute = location.pathname.startsWith('/search')
  const isDesktop = useDesktopMediaQuery()
  const isCompact = !isDesktop
  const { trackSearchEvent } = useSearchTracking()
  const platform = isCompact
    ? SEARCH_PLATFORMS.MOBILE
    : SEARCH_PLATFORMS.DESKTOP
  const searchInput = useRef(null)
  const [redirectSearch, setRedirectSearch] = useState(false)
  const [searched, setSearched] = useState(searchedProp || '')
  const { ready: featureFlagsReady } = useFeatureFlags()
  const showAIFilter = featureFlagsReady ? checkFlag('showAIFilter') : false
  const defaultSearchValue = showAIFilter
    ? t('search.use-ai-search')
    : t('home.search')

  const [searchValue, setSearchValue] = useState(
    searchedProp || defaultSearchValue
  )
  const prevDefaultSearchValueRef = useRef(defaultSearchValue)
  const [isSearching, setIsSearching] = useState(false)
  // Mobile-only: show/hide the search bar itself; keep visible by default
  const [showMobileSearchBar, setShowMobileSearchBar] = useState(isSearchRoute)

  const prevSearchedRef = useRef(searchedProp)
  const prevIsSearchingRef = useRef(isSearching)

  // Sync with external searched / redirectSearch equivalent of componentDidUpdate
  useEffect(() => {
    if (
      redirectSearch ||
      prevSearchedRef.current !== searchedProp ||
      prevIsSearchingRef.current !== isSearching
    ) {
      if (isSearching) {
        setSearched('')
        setSearchValue('')
        setRedirectSearch(false)
      } else {
        setSearched(searchedProp)
        setSearchValue(searchedProp || defaultSearchValue)
        setRedirectSearch(false)
      }
    }
    prevSearchedRef.current = searchedProp
    prevIsSearchingRef.current = isSearching
  }, [redirectSearch, searchedProp, isSearching, defaultSearchValue])

  useEffect(() => {
    if (
      !isSearching &&
      searchValue === prevDefaultSearchValueRef.current &&
      searchValue !== defaultSearchValue
    ) {
      setSearchValue(defaultSearchValue)
    }
    prevDefaultSearchValueRef.current = defaultSearchValue
  }, [defaultSearchValue, isSearching, searchValue])

  // Focus handled inside TextField's onFocus path via state updates

  const onSearchChange = (value) => {
    if (value === '' && !isSearchRoute) {
      setIsSearching(true)
      onSearchingChange && onSearchingChange(true)
    }
    setSearchValue(value)
  }

  const executeSearch = () => {
    if (searchValue !== '') {
      setRedirectSearch(true)
      setSearched(encodeURIComponent(searchValue))
      setIsSearching(false)
      onSearchingChange && onSearchingChange(false)
    }
  }

  const debouncedTrackFocus = useMemo(
    () =>
      debounce((value) => {
        trackSearchEvent?.({
          event: SEARCH_TRACKING_EVENTS.SEARCH_FOCUS,
          action: SEARCH_ACTIONS.FOCUS,
          label: value || '',
          platform,
          source: 'releases_menu',
          source_component: 'releases_menu_input',
          is_search_route: String(isSearchRoute),
          search_query_length: (value || '').length,
          type: 'search'
        })
      }, 300),
    [trackSearchEvent, platform, isSearchRoute]
  )

  const handleOnSearchChange = (value) => {
    onSearchChange(value)
  }

  useEffect(() => {
    return () => {
      debouncedTrackFocus.cancel && debouncedTrackFocus.cancel()
    }
  }, [debouncedTrackFocus])

  useEffect(() => {
    if (isSearchRoute) {
      setShowMobileSearchBar(true)
    } else {
      setShowMobileSearchBar(false)
    }
  }, [isSearchRoute])

  const aiSearchPlaceholder = isCompact
    ? t('search.ai-search-placeholder-mobile')
    : t('search.ai-search-placeholder')

  const currentPlaceholder = showAIFilter
    ? aiSearchPlaceholder
    : t('home.search')

  const SearchField = (
    <ReleasesSearchBar
      inputRef={searchInput}
      isMobile={isCompact}
      isSearching={isSearching}
      isSearchRoute={isSearchRoute}
      value={searchValue}
      placeholder={isSearching && currentPlaceholder}
      onChange={handleOnSearchChange}
      onFocus={() => {
        if (!isSearchRoute && !isSearching) {
          setIsSearching(true)
          onSearchingChange && onSearchingChange(true)
          trackSearchEvent?.({
            event: SEARCH_TRACKING_EVENTS.SEARCH_BAR_VISIBILITY,
            action: SEARCH_ACTIONS.SHOW_BAR,
            label: '',
            platform,
            source: 'releases_menu',
            type: 'search_bar',
            visibility_state: 'shown',
            is_search_route: String(isSearchRoute)
          })
        }
      }}
      onEnter={executeSearch}
      onBack={() => {
        if (isSearchRoute) {
          navigate(location.state?.searchBackTo || '/releases', {
            replace: true
          })
        } else {
          setShowMobileSearchBar(false)
          setIsSearching(false)
          onSearchingChange && onSearchingChange(false)
          trackSearchEvent?.({
            event: SEARCH_TRACKING_EVENTS.SEARCH_BAR_VISIBILITY,
            action: SEARCH_ACTIONS.HIDE_BAR,
            label: '',
            platform,
            source: 'releases_menu',
            type: 'search_bar',
            visibility_state: 'hidden',
            is_search_route: String(isSearchRoute)
          })
        }
        setSearchValue(defaultSearchValue)
      }}
    />
  )

  const renderOnDesktop = () => {
    return (
      <DesktopLayout>
        <HeaderRow>
          <LeftGroup>
            {redirectSearch && (
              <Navigate push to={`/search/top?q=${searched}`} />
            )}
            {SearchField}
          </LeftGroup>
          <GlobalActionsWrapper>
            <GlobalActionMenu />
          </GlobalActionsWrapper>
        </HeaderRow>
        <MenuOptions isSearching={isSearching} />
      </DesktopLayout>
    )
  }
  const renderOnCompact = () => {
    return (
      <MobileLayout>
        <HeaderRow>
          <LeftGroup>
            {' '}
            <HamburguerMenu style={{ marginRight: 20 }} />
          </LeftGroup>
          <GlobalActionsWrapper>
            <GlobalActionMenu
              showMobileSearchIcon={!showMobileSearchBar}
              onMobileSearchClick={() => {
                setShowMobileSearchBar(true)
                if (!isSearchRoute && !isSearching) {
                  setIsSearching(true)
                  onSearchingChange && onSearchingChange(true)
                  trackSearchEvent?.({
                    event: SEARCH_TRACKING_EVENTS.SEARCH_BAR_VISIBILITY,
                    action: SEARCH_ACTIONS.SHOW_BAR,
                    label: '',
                    platform,
                    source: 'releases_menu',
                    type: 'search_bar',
                    visibility_state: 'shown',
                    is_search_route: String(isSearchRoute)
                  })
                }
                // focus input shortly after becoming visible
                setTimeout(() => {
                  if (searchInput.current) searchInput.current.focus()
                }, 50)
              }}
            />
          </GlobalActionsWrapper>
        </HeaderRow>
        {redirectSearch && <Navigate push to={`/search/top?q=${searched}`} />}
        {showMobileSearchBar && SearchField}
        <MenuOptions isSearching={isSearching} />
      </MobileLayout>
    )
  }

  return (
    <Container>{isCompact ? renderOnCompact() : renderOnDesktop()}</Container>
  )
}

ReleasesMenu.propTypes = {
  searched: PropTypes.string,
  onSearchingChange: PropTypes.func
}

export default ReleasesMenu
